import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { saveAs } from 'file-saver';

import useTranslation from 'customHooks/translate';
import { getCreditors } from 'services/partners';
import {
  getReportCreditorCustomerBalance,
  getReportCreditorCustomerBalanceSummary,
  getCustomerBalanceInfoCreditorPdf,
} from 'services/reports';
import CustomDialog from 'pages/Dialogs/CustomDialog';
import {
  CREDITORS,
  REPORTS_EXPENSES,
  REPORTS_EXPENSES_SUMMARY,
} from 'events/types';

import { ReportsSelect } from '../components/reportsSelect';
import { ReportsTab } from '../components/ReportsTab';
import ExpensesTable from './ExpensesTable';
import { ExpensesSummary } from './ExpensesSummary';
import { delay, openNotification, waitForReportCompletion } from 'utils';
import { CUSTOMER_CREDITOR_BALANCE_REPORT_KEY } from 'consts';

export const CustomerCreditorBalance = () => {
  const translation = useTranslation();
  const [toDate, setToDate] = useState('');
  const [partner, setPartner] = useState(0);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [confirmedToDate, setConfirmedToDate] = useState('');
  const [confirmedPartner, setConfirmedPartner] = useState(0);

  const [pageSize] = useState(10);

  // Infinite scroll related states
  const [expensesPageIndex, setExpensesPageIndex] = useState(0);
  const [expensesTableData, setExpensesTableData] = useState<any[]>([]);
  const [hasMoreExpenses, setHasMoreExpenses] = useState(true);
  const [isLoadingMoreExpenses, setIsLoadingMoreExpenses] = useState(false);
  const [open, setOpen] = useState<boolean>(true);

  // Refs for scroll containers and loading states
  const expensesTableContainerRef = useRef<HTMLDivElement>(null);
  const expensesLoadingRef = useRef<HTMLDivElement>(null);
  const isFetchingExpensesRef = useRef(false);

  const handleSelect = (value: unknown) => {
    setPartner(Number(value));
  };

  // Reset data function
  const resetData = () => {
    setExpensesTableData([]);
    setExpensesPageIndex(0);
    setHasMoreExpenses(true);
  };

  const { data: creditorsData } = useQuery({
    queryKey: [CREDITORS],
    queryFn: () => getCreditors(),
    select: (response) => {
      return response.data.data.map((creditor) => ({
        label: creditor.name,
        value: creditor.id,
      }));
    },
  });

  // Function to fetch expenses data
  const fetchExpensesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingExpensesRef.current) return null;

      isFetchingExpensesRef.current = true;
      setIsLoadingMoreExpenses(true);

      try {
        const response = await getReportCreditorCustomerBalance(
          confirmedToDate,
          confirmedPartner,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setExpensesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataExpense}`);
            } else {
              openNotification(
                `${translation.reports.ExpencesDataFound}`,
                'success'
              );
            }
          } else {
            setExpensesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : expensesTableData.length) + data.data.length;
          setHasMoreExpenses(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataExpense}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreExpenses(false);
        isFetchingExpensesRef.current = false;
      }
    },
    [
      confirmedToDate,
      confirmedPartner,
      pageSize,
      expensesTableData.length,
      translation.reports.NoDataExpense,
      translation.reports.ExpencesDataFound,
    ]
  );

  useQuery({
    queryKey: [REPORTS_EXPENSES, confirmedToDate, confirmedPartner],
    queryFn: () => fetchExpensesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  const { data: summaryData } = useQuery({
    queryKey: [REPORTS_EXPENSES_SUMMARY, confirmedToDate, confirmedPartner],
    queryFn: () =>
      getReportCreditorCustomerBalanceSummary(
        confirmedToDate,
        confirmedPartner
      ),
    select: (res) => res.data,
    enabled: shouldFetch,
  });

  const setDateRange = (from: string, to: string) => {
    setToDate(to);
  };

  const handleConfirm = () => {
    if (!toDate || !partner) {
      openNotification(`${translation.reports.Parameters}`, 'error');
      return;
    }
    setConfirmedToDate(toDate);
    setConfirmedPartner(partner);
    resetData();
    setShouldFetch(true);
    setOpen(false);
  };

  const handlePdfExport = async () => {
    try {
      openNotification(
        `${translation.reports.PdfWait}`,
        'info',
        0,
        CUSTOMER_CREDITOR_BALANCE_REPORT_KEY,
        true
      );

      const excelResponse = await getCustomerBalanceInfoCreditorPdf(
        confirmedToDate,
        confirmedPartner
      );

      const reportId = excelResponse?.data?.id;

      if (reportId) {
        await delay(4000);
        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );

        const blob = new Blob([downloadResponse.data], {
          type: 'application/pdf',
        });
        saveAs(blob, `report_${reportId}.pdf`);

        openNotification(
          `${translation.reports.PdfGenerated}`,
          'success',
          2,
          CUSTOMER_CREDITOR_BALANCE_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          CUSTOMER_CREDITOR_BALANCE_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        CUSTOMER_CREDITOR_BALANCE_REPORT_KEY
      );
    }
  };

  // Handle expenses scroll
  const handleExpensesScroll = useCallback(() => {
    if (
      !expensesTableContainerRef.current ||
      !expensesLoadingRef.current ||
      isFetchingExpensesRef.current ||
      !hasMoreExpenses
    )
      return;

    const containerRect =
      expensesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = expensesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = expensesPageIndex + 1;
      setExpensesPageIndex(nextPage);
      fetchExpensesData(nextPage);
    }
  }, [expensesPageIndex, hasMoreExpenses, fetchExpensesData]);

  useEffect(() => {
    const expensesContainer = expensesTableContainerRef.current;
    if (expensesContainer) {
      expensesContainer.addEventListener('scroll', handleExpensesScroll);
      return () => {
        expensesContainer.removeEventListener('scroll', handleExpensesScroll);
      };
    }
  }, [handleExpensesScroll]);

  const isShowButtons = expensesTableData?.length > 0;

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
      <div className="col-span-4 mb-[20px] md:mb-[0px]">
        {(!!confirmedToDate || !!confirmedPartner) && (
          <div className="my-[12px] px-[16px] md:px-[32px]">
            <div className="flex flex-wrap items-center gap-[8px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[12px] text-[#4B4B4B] md:text-[13px]">
              <span className="font-semibold text-[#1D1D1D]">
                {translation.filter}:
              </span>
              {confirmedToDate && (
                <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                  {confirmedToDate}
                </span>
              )}
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.subusers.creditor}:{' '}
                </span>
                {
                  creditorsData?.find((option) => partner === option.value)
                    ?.label
                }
              </span>
            </div>
          </div>
        )}
        <ExpensesTable
          expensesTableContainerRef={expensesTableContainerRef}
          expensesTableData={expensesTableData}
          expensesLoadingRef={expensesLoadingRef}
          isLoadingMoreExpenses={isLoadingMoreExpenses}
          hasMoreExpenses={hasMoreExpenses}
          setOpen={setOpen}
        />
      </div>
      {isShowButtons && (
        <div className="h-full  md:border-l md:border-l-[#f5f7f9]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px] px-[16px] md:pt-[12px]">
            <button
              onClick={handlePdfExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsPdf}</p>
            </button>
          </div>
          {!!summaryData && (
            <ExpensesSummary
              title={translation.reports.SummaryExpence}
              summaryData={summaryData}
            />
          )}
        </div>
      )}

      <CustomDialog
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        title={translation.filter}
        buttonTitle={translation.reports.BtnConfirm}
        onSubmit={handleConfirm}
      >
        <ReportsTab
          setDateRange={setDateRange}
          hasFromDateField={false}
          hasTabs={false}
        />
        <ReportsSelect
          title={translation.subusers.creditor}
          onChange={handleSelect}
          options={creditorsData || []}
        />
      </CustomDialog>
    </div>
  );
};

export default CustomerCreditorBalance;
